import type {PageProps} from 'gatsby'
import {graphql} from 'gatsby'
import React from 'react'

import FeatureBlock from '@/components/feature-block'
import Footer from '@/components/footer'
import Header from '@/components/header'
import SEO from '@/components/seo'
import type {JobListingQuery} from '@/graphql-types'
import {JobListingWithFilters} from '@/sections/job-listing-with-filters'
import {Steps} from '@/sections/steps'
import handleNoPrismicData from '@/utils/handle-no-prismic-data'
import {withPrismicPreview} from '@/utils/with-prismic-preview'
import {Main} from '@helios/shared/components/skip-to-content'
import {Section} from '@helios/shared/styles/grid'
import type {GatsbyHeadProps} from '@helios/shared/types/gatsby-head'

type Props = PageProps<JobListingQuery>

const JobListing = (props: Props) => {
    const data = props.data.prismicJobListing.data
    const allJobs = props.data.allGreenhouseJob.nodes
    const headings = data.listing_table_headings[0]

    if (!data) {
        return handleNoPrismicData()
    }

    return (
        <>
            <Header hideRightNav />

            <Main>
                <Section data-testid="jobs-page-hero">
                    <FeatureBlock
                        titleVariant="heading-large"
                        title={data.hero[0].title}
                        image={data.hero[0].image}
                        body={data.hero[0].body}
                    />
                </Section>

                <Section>
                    <JobListingWithFilters allJobs={allJobs} headings={{...headings}} />
                </Section>

                <Steps
                    title={data.steps_title}
                    showCount={true}
                    items={data.steps}
                    ctaLink={data.cta_link}
                    ctaText={data.cta_text}
                    dataTestId="jobs-page-steps"
                />
            </Main>

            <Footer />
        </>
    )
}

export const Head = ({location, pageContext, data}: GatsbyHeadProps<JobListingQuery>) => {
    const pageData = data.prismicJobListing.data

    return (
        <SEO
            location={location}
            pageContext={pageContext}
            title={pageData.meta_title}
            description={pageData.meta_description}
            openGraphImageUrl={pageData.open_graph_image?.url}
        />
    )
}

export const query = graphql`
    query JobListing($locale: String!, $imageBreakpoints: [Int], $twoColumnsAsymmetric: String) {
        prismicJobListing(lang: {eq: $locale}) {
            data {
                meta_title
                meta_description
                open_graph_image {
                    url
                }
                hero {
                    title
                    body
                    image {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                        gatsbyImageData(
                            breakpoints: $imageBreakpoints
                            sizes: $twoColumnsAsymmetric
                        )
                    }
                }
                steps_title
                cta_link {
                    ...PrismicLink
                }
                cta_text
                steps {
                    heading
                    content
                    background_color
                    image {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                        gatsbyImageData
                    }
                }
                listing_table_headings {
                    team
                    role
                    location
                    search_input_placeholder
                    all_departments_label
                    all_departments_placeholder
                    no_search_label
                    no_results_label
                    no_jobs_found
                    showing_roles_by_department
                    showing_roles_by_department_with_search
                    clear_filters
                }
            }
        }
        allGreenhouseJob {
            nodes {
                id
                gh_Id
                title
                absolute_url
                offices {
                    name
                }
                metadata {
                    id
                    value
                }
                departments {
                    name
                }
            }
        }
    }
`

export default withPrismicPreview(JobListing)
